import { useApiService } from "@art/shared-ui";
import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import { usePageWrapper } from "../../../hooks";


function EmailConfigOAuthCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const apiService = useApiService();
    const exchangeStarted = useRef(false);

    useEffect(() => {
        if (exchangeStarted.current) return; // React 18 StrictMode double-mount guard
        exchangeStarted.current = true;


        const stashed = sessionStorage.getItem("email_oauth_callback");
        sessionStorage.removeItem("email_oauth_callback"); // always clear: no loops
        const stashedParams = stashed ? new URLSearchParams(stashed) : null;

        const code = searchParams.get("code") ?? stashedParams?.get("code") ?? null;
        const state = searchParams.get("state") ?? stashedParams?.get("state") ?? null;
        const oauthError = searchParams.get("error") ?? stashedParams?.get("error") ?? null;

        const finish = () => navigate("/application-settings/messaging/email", { replace: true });

        if (oauthError) {
            apiService.showSnack(
                oauthError === "access_denied"
                    ? "Google connection was cancelled"
                    : `Google connection failed: ${oauthError}`,
                "error"
            );
            finish();
            return;
        }
        if (!code || !state) {
            apiService.showSnack("Invalid Google response — try connecting again", "error");
            finish();
            return;
        }

        const exchange = async () => {
            try {
                const response = await apiService.post("/notification/email-configuration/oauth/google/exchange", {
                    code,
                    state,
                });
                const connectedEmail = response?.data?.data?.connected_email;
                apiService.showSnack(
                    `Google account connected${connectedEmail ? `: ${connectedEmail}` : ""}`,
                    "success"
                );
            } catch (error: any) {
                apiService.showSnack(
                    error?.response?.data?.message || "Failed to connect the Google account",
                    "error"
                );
            } finally {
                finish();
            }
        };
        exchange();
    }, []);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 10 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
                Connecting your Google account…
            </Typography>
        </Box>
    );
}

export default usePageWrapper(EmailConfigOAuthCallback, {
    page_title: "Email Configuration",
});
